import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import {
  createRole,
  deleteRole,
  fetchAllPermissions,
  fetchRoles,
  updateRole,
} from "../../api/rbac";
import ConfirmationBox from "../../componentLibrary/ConfirmationBox";
import { useNotification } from "../../contexts/NotificationContext";
import { APIError } from "../../utils/ErrorHandling";
import {
  CreateRoleRequest,
  ListRolesResponse,
  Permission,
  Role,
  UpdateRoleRequest,
} from "../../types/rbac";
import RolePermissionManagementView from "./components/RolePermissionManagementView";

const RolePermissionManagementPage = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();

  const [search, setSearch] = useState("");
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  const [formData, setFormData] = useState<{
    name: string;
    displayName: string;
    description: string;
    permissions: string[];
  }>({
    name: "",
    displayName: "",
    description: "",
    permissions: [],
  });

  const {
    data: rolesData,
    isLoading: loadingRoles,
    error: rolesError,
  } = useQuery<ListRolesResponse, APIError>({
    queryKey: ["roles", search],
    queryFn: () => fetchRoles({ search: search || undefined, pageSize: 100 }),
  });

  const { data: permissionsData, isLoading: loadingPermissions } = useQuery({
    queryKey: ["permissions"],
    queryFn: fetchAllPermissions,
  });

  const createRoleMutation = useMutation({
    mutationFn: (data: CreateRoleRequest) => createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      addNotification("success", "Role created successfully");
      setIsCreateModalOpen(false);
      resetForm();
    },
    onError: (error: APIError) => {
      addNotification("error", error.message || "Failed to create role");
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleRequest }) =>
      updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      addNotification("success", "Role updated successfully");
      setIsEditModalOpen(false);
      setSelectedRole(null);
      resetForm();
    },
    onError: (error: APIError) => {
      addNotification("error", error.message || "Failed to update role");
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: ({ id, force }: { id: string; force: boolean }) =>
      deleteRole(id, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      addNotification("success", "Role deleted successfully");
      setSelectedRole(null);
    },
    onError: (error: APIError) => {
      addNotification("error", error.message || "Failed to delete role");
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      displayName: "",
      description: "",
      permissions: [],
    });
  };

  const handleCreateRole = useCallback(() => {
    if (!formData.name || !formData.displayName) {
      addNotification("error", "Name and display name are required");
      return;
    }

    createRoleMutation.mutate({
      name: formData.name,
      displayName: formData.displayName,
      description: formData.description,
      permissions: formData.permissions,
    });
  }, [formData, createRoleMutation]);

  // Handle update role
  const handleUpdateRole = useCallback(() => {
    if (!selectedRole) return;

    updateRoleMutation.mutate({
      id: selectedRole._id,
      data: {
        displayName: formData.displayName,
        description: formData.description,
        permissions: formData.permissions,
      },
    });
  }, [selectedRole, formData, updateRoleMutation]);

  const handleDeleteRole = useCallback((role: Role) => {
    setRoleToDelete(role);
    setIsDeleteConfirmOpen(true);
  }, []);

  const confirmDeleteRole = useCallback(() => {
    if (roleToDelete) {
      deleteRoleMutation.mutate({ id: roleToDelete._id, force: false });
      setIsDeleteConfirmOpen(false);
      setRoleToDelete(null);
    }
  }, [roleToDelete, deleteRoleMutation]);

  const cancelDeleteRole = useCallback(() => {
    setIsDeleteConfirmOpen(false);
    setRoleToDelete(null);
  }, []);

  const openCreateModal = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const openEditModal = (role: Role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      displayName: role.displayName,
      description: role.description,
      permissions: role.permissions,
    });
    setIsEditModalOpen(true);
  };

  const togglePermission = (permissionId: string) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter((p) => p !== permissionId)
        : [...prev.permissions, permissionId],
    }));
  };

  const handleFormChange = (data: Partial<typeof formData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const roles = rolesData?.items || [];
  const allPermissions = permissionsData?.permissions || [];

  const groupedPermissions = allPermissions.reduce(
    (acc, perm) => {
      if (!acc[perm.group]) {
        acc[perm.group] = [];
      }
      acc[perm.group].push(perm);
      return acc;
    },
    {} as Record<string, Permission[]>,
  );

  return (
    <>
      <RolePermissionManagementView
        roles={roles}
        selectedRole={selectedRole}
        search={search}
        isLoadingRoles={loadingRoles}
        rolesError={rolesError as APIError | null}
        allPermissions={allPermissions}
        groupedPermissions={groupedPermissions}
        isLoadingPermissions={loadingPermissions}
        isCreateModalOpen={isCreateModalOpen}
        isEditModalOpen={isEditModalOpen}
        formData={formData}
        isCreating={createRoleMutation.isPending}
        isUpdating={updateRoleMutation.isPending}
        onSearchChange={setSearch}
        onRoleSelect={setSelectedRole}
        onOpenCreateModal={openCreateModal}
        onOpenEditModal={openEditModal}
        onDeleteRole={handleDeleteRole}
        onCloseCreateModal={() => setIsCreateModalOpen(false)}
        onCloseEditModal={() => setIsEditModalOpen(false)}
        onCreateRole={handleCreateRole}
        onUpdateRole={handleUpdateRole}
        onFormChange={handleFormChange}
        onTogglePermission={togglePermission}
      />
      <ConfirmationBox
        isOpen={isDeleteConfirmOpen}
        message={
          <p>
            Are you sure you want to delete the role:{" "}
            <span className="font-bold">{roleToDelete?.displayName}</span>? This
            action cannot be undone.
          </p>
        }
        onConfirm={confirmDeleteRole}
        onCancel={cancelDeleteRole}
        confirmText="Yes, Delete"
        cancelText="No, Cancel"
        title="Delete Role"
        danger={true}
      />
    </>
  );
};

export default RolePermissionManagementPage;
